import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DateBlockingPanel = ({ blockedDates, onBlockedDatesChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [newBlockDate, setNewBlockDate] = useState('');
  const [newBlockReason, setNewBlockReason] = useState('إجازة شخصية');
  const [blockType, setBlockType] = useState('personal');

  const reasonOptions = [
    { value: 'personal', label: 'إجازة شخصية' },
    { value: 'maintenance', label: 'صيانة المعدات' },
    { value: 'private', label: 'حجز خاص' },
    { value: 'other', label: 'أخرى' }
  ];

  const handleAddBlockedDate = () => {
    if (!newBlockDate) {
      alert('الرجاء اختيار تاريخ');
      return;
    }

    const newBlock = {
      date: newBlockDate,
      reason: newBlockReason,
      type: blockType
    };

    onBlockedDatesChange([...blockedDates, newBlock]);
    setNewBlockDate('');
    setNewBlockReason('إجازة شخصية');
    setBlockType('personal');
  };

  const handleRemoveBlockedDate = (dateToRemove) => {
    onBlockedDatesChange(blockedDates?.filter(bd => bd?.date !== dateToRemove));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'personal':
        return 'from-blue-500 to-cyan-500';
      case 'maintenance':
        return 'from-orange-500 to-red-500';
      case 'private':
        return 'from-primary to-primary/80';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const formatArabicDate = (dateString) => {
    const date = new Date(dateString);
    const arabicMonths = [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    return `${date?.getDate()} ${arabicMonths?.[date?.getMonth()]} ${date?.getFullYear()}`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-rose-500">
            <Icon name="XCircle" size={20} className="text-white" />
          </div>
          <div className="text-right">
            <h3 className="text-lg font-bold text-slate-900">حجب التواريخ</h3>
            <p className="text-sm text-slate-600">إدارة الأيام غير المتاحة</p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          className="text-slate-600" 
        />
      </button>
      {/* Content */}
      {isExpanded && (
        <div className="p-6 pt-0 space-y-6">
          {/* Add New Block */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-red-500 to-rose-500 rounded-full"></div>
              <h4 className="font-semibold text-slate-900">إضافة تاريخ محجوب</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                التاريخ
              </label>
              <Input
                type="date"
                value={newBlockDate}
                onChange={(e) => setNewBlockDate(e?.target?.value)}
                className="w-full"
                min={new Date()?.toISOString()?.split('T')?.[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                السبب
              </label>
              <Select
                value={blockType}
                onChange={(e) => {
                  setBlockType(e?.target?.value);
                  const selected = reasonOptions?.find(opt => opt?.value === e?.target?.value);
                  setNewBlockReason(selected?.label || '');
                }}
                className="w-full"
              >
                {reasonOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </Select>
            </div>

            <Button
              onClick={handleAddBlockedDate}
              className="w-full bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white"
            >
              <Icon name="Plus" size={18} />
              إضافة تاريخ محجوب
            </Button>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200"></div>

          {/* Blocked Dates List */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-slate-500 to-slate-600 rounded-full"></div>
              <h4 className="font-semibold text-slate-900">التواريخ المحجوبة</h4>
            </div>

            {blockedDates && blockedDates?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {blockedDates?.map((block, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${getTypeColor(block?.type)}`}></div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {formatArabicDate(block?.date)}
                        </p>
                        <p className="text-sm text-slate-600">{block?.reason}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveBlockedDate(block?.date)}
                      className="p-2 rounded-lg hover:bg-red-100 transition-colors duration-200 group"
                    >
                      <Icon name="Trash2" size={16} className="text-slate-400 group-hover:text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Calendar" size={48} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">لا توجد تواريخ محجوبة</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateBlockingPanel;